import { supabase } from '../lib/supabase';

export interface SyncResult {
  success: boolean;
  articlesAdded: number;
  articlesUpdated: number;
  articlesRemoved: number;
  totalSynced: number;
  durationMs: number;
  error?: string;
}

export interface SyncOptions {
  tenantId?: string;
  projectId?: string;
  forceFullSync?: boolean;
  lang?: string;
  currency?: string;
  targetCountries?: string[];
}

export type SyncPhase = 'pending' | 'fetching_countries' | 'filtering' | 'syncing_details' | 'cleanup' | 'completed' | 'failed';

export interface SyncJob {
  id: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'paused';
  phase: SyncPhase;
  phaseMessage?: string;
  progress: number;
  totalCount: number;
  articlesAdded: number;
  articlesUpdated: number;
  articlesRemoved: number;
  articlesFiltered?: number;
  articlesSkipped?: number;
  targetCountries?: string[];
  errorMessage?: string;
  startedAt?: string;
  completedAt?: string;
  createdAt: string;
  canResume?: boolean;
  resumeCount?: number;
  lastProcessedIndex?: number;
}

export interface StartSyncResult {
  success: boolean;
  jobId?: string;
  error?: string;
}

export interface ResumeSyncResult {
  success: boolean;
  jobId?: string;
  resumeCount?: number;
  error?: string;
}

export interface CancelSyncResult {
  success: boolean;
  error?: string;
}

export class VoucherSyncService {
  async syncCatalog(options: SyncOptions = {}): Promise<SyncResult> {
    const startTime = Date.now();
    const result: SyncResult = {
      success: false,
      articlesAdded: 0,
      articlesUpdated: 0,
      articlesRemoved: 0,
      totalSynced: 0,
      durationMs: 0,
    };

    try {
      console.log('[VoucherSync] Starting catalog sync via Edge Function...');

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No active session. Please log in.');
      }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      if (!supabaseUrl) {
        throw new Error('Supabase URL is not configured.');
      }

      const edgeFunctionUrl = `${supabaseUrl}/functions/v1/voucher-operations/sync-catalog`;

      console.log('[VoucherSync] Calling Edge Function:', edgeFunctionUrl);

      const response = await fetch(edgeFunctionUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          tenantId: options.tenantId,
          forceFullSync: options.forceFullSync,
          lang: options.lang || 'en',
          currency: options.currency || 'USD',
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[VoucherSync] Edge Function error:', errorText);
        throw new Error(`Sync failed: ${response.statusText} - ${errorText}`);
      }

      const syncResult = await response.json();
      console.log('[VoucherSync] Sync result from Edge Function:', syncResult);

      result.success = syncResult.success || false;
      result.totalSynced = syncResult.totalSynced || 0;
      result.articlesAdded = syncResult.articlesAdded || 0;
      result.articlesUpdated = syncResult.articlesUpdated || 0;
      result.articlesRemoved = syncResult.articlesRemoved || 0;
      result.durationMs = Date.now() - startTime;

      console.log('[VoucherSync] Sync completed:', result);

      return result;
    } catch (error: unknown) {
      result.success = false;
      result.error = error instanceof Error ? error.message : String(error);
      result.durationMs = Date.now() - startTime;

      console.error('[VoucherSync] Sync failed:', error);

      throw error;
    }
  }

  async getLastSyncStatus(tenantId?: string): Promise<unknown> {
    const query = supabase
      .from('voucher_sync_log')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(1);

    if (tenantId) {
      query.eq('tenant_id', tenantId);
    }

    const { data } = await query.maybeSingle();
    return data;
  }

  async getSyncHistory(tenantId?: string, limit = 10): Promise<unknown[]> {
    const query = supabase
      .from('voucher_sync_log')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(limit);

    if (tenantId) {
      query.eq('tenant_id', tenantId);
    }

    const { data } = await query;
    return data || [];
  }

  async startBackgroundSync(options: SyncOptions = {}): Promise<StartSyncResult> {
    try {
      console.log('[VoucherSync] Starting background sync job...', options);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No active session. Please log in.');
      }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      if (!supabaseUrl) {
        throw new Error('Supabase URL is not configured.');
      }

      const edgeFunctionUrl = `${supabaseUrl}/functions/v1/voucher-operations/start-sync`;

      const response = await fetch(edgeFunctionUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          tenantId: options.tenantId,
          projectId: options.projectId,
          lang: options.lang || 'en',
          currency: options.currency || 'USD',
          targetCountries: options.targetCountries || [],
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to start sync: ${response.statusText} - ${errorText}`);
      }

      const result = await response.json();
      console.log('[VoucherSync] Background sync started:', result);

      return {
        success: true,
        jobId: result.jobId,
      };
    } catch (error: unknown) {
      console.error('[VoucherSync] Failed to start background sync:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : String(error),
      };
    }
  }

  async getSyncJobStatus(jobId: string): Promise<SyncJob | null> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No active session');
      }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      if (!supabaseUrl) {
        throw new Error('Supabase URL is not configured');
      }

      const edgeFunctionUrl = `${supabaseUrl}/functions/v1/voucher-operations/sync-status/${jobId}`;

      const response = await fetch(edgeFunctionUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        throw new Error(`Failed to get sync status: ${response.statusText}`);
      }

      const result = await response.json();
      return result.job as SyncJob;
    } catch (error: unknown) {
      console.error('[VoucherSync] Failed to get sync status:', error);
      return null;
    }
  }

  async resumeSync(jobId: string): Promise<ResumeSyncResult> {
    try {
      console.log('[VoucherSync] Resuming sync job:', jobId);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No active session. Please log in.');
      }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      if (!supabaseUrl) {
        throw new Error('Supabase URL is not configured.');
      }

      const edgeFunctionUrl = `${supabaseUrl}/functions/v1/voucher-operations/resume-sync`;

      const response = await fetch(edgeFunctionUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ jobId }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to resume sync: ${response.statusText} - ${errorText}`);
      }

      const result = await response.json();
      console.log('[VoucherSync] Sync resumed:', result);

      return {
        success: true,
        jobId: result.jobId,
        resumeCount: result.resumeCount,
      };
    } catch (error: unknown) {
      console.error('[VoucherSync] Failed to resume sync:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : String(error),
      };
    }
  }

  async cancelSync(jobId: string): Promise<CancelSyncResult> {
    try {
      console.log('[VoucherSync] Cancelling sync job:', jobId);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No active session. Please log in.');
      }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      if (!supabaseUrl) {
        throw new Error('Supabase URL is not configured.');
      }

      const edgeFunctionUrl = `${supabaseUrl}/functions/v1/voucher-operations/cancel-sync`;

      const response = await fetch(edgeFunctionUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ jobId }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to cancel sync: ${response.statusText} - ${errorText}`);
      }

      console.log('[VoucherSync] Sync cancelled successfully');

      return { success: true };
    } catch (error: unknown) {
      console.error('[VoucherSync] Failed to cancel sync:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : String(error),
      };
    }
  }

  pollSyncJob(
    jobId: string,
    onProgress: (job: SyncJob) => void,
    onComplete: (job: SyncJob) => void,
    onError: (error: string) => void,
    onPaused?: (job: SyncJob) => void,
    intervalMs = 2000
  ): () => void {
    let isPolling = true;

    const poll = async () => {
      if (!isPolling) return;

      const job = await this.getSyncJobStatus(jobId);

      if (!job) {
        onError('Job not found');
        return;
      }

      onProgress(job);

      if (job.status === 'completed') {
        onComplete(job);
        return;
      }

      if (job.status === 'failed') {
        onError(job.errorMessage || 'Sync job failed');
        return;
      }

      if (job.status === 'paused') {
        if (onPaused) {
          onPaused(job);
        }
        return;
      }

      if (isPolling) {
        setTimeout(poll, intervalMs);
      }
    };

    poll();

    return () => {
      isPolling = false;
    };
  }

  getPhaseLabel(phase: SyncPhase): string {
    const labels: Record<SyncPhase, string> = {
      pending: 'Initializing...',
      fetching_countries: 'Fetching article list...',
      filtering: 'Filtering by country...',
      syncing_details: 'Syncing voucher details...',
      cleanup: 'Cleaning up...',
      completed: 'Completed',
      failed: 'Failed',
    };
    return labels[phase] || phase;
  }

  getStatusLabel(status: SyncJob['status']): string {
    const labels: Record<SyncJob['status'], string> = {
      pending: 'Pending',
      running: 'Running',
      completed: 'Completed',
      failed: 'Failed',
      paused: 'Paused - Resume to continue',
    };
    return labels[status] || status;
  }

  getPhaseProgress(job: SyncJob): { current: number; total: number; percentage: number } {
    const { phase, progress, totalCount, articlesFiltered } = job;

    switch (phase) {
      case 'fetching_countries':
        return {
          current: progress,
          total: totalCount || progress,
          percentage: totalCount > 0 ? Math.round((progress / totalCount) * 100) : 0,
        };
      case 'filtering':
        return {
          current: progress,
          total: totalCount,
          percentage: 50,
        };
      case 'syncing_details':
        const detailTotal = articlesFiltered || totalCount;
        return {
          current: progress,
          total: detailTotal,
          percentage: detailTotal > 0 ? Math.round((progress / detailTotal) * 100) : 0,
        };
      case 'cleanup':
        return {
          current: progress,
          total: articlesFiltered || totalCount,
          percentage: 95,
        };
      case 'completed':
        return {
          current: articlesFiltered || progress,
          total: articlesFiltered || progress,
          percentage: 100,
        };
      default:
        return {
          current: progress,
          total: totalCount,
          percentage: 0,
        };
    }
  }
}

export const voucherSyncService = new VoucherSyncService();
